import React, { useEffect, useState } from "react"
import { Button, Stack, Typography } from "@mui/material"
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined"
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import { useCompany, useSnackbar, useUser } from "src/providers"
import { Community } from "src/models/user"
import { getUserCommunities } from "src/services/api/user"
import { AddGatedCommunityDialog } from "./AddGatedCommunityDialog"
import { AddMembersToCommunityDialog } from "./AddMembersToCommunityDialog"
import { DeactivateMemberDialog } from "./DeactivateMemberDialog"
import { type CompanyMembersSortBy, deleteInvitation, resendInvitation } from "src/services/api/companyMembers"
import {
  type GatedCommunityMemberRecord,
  type GatedCommunityMembersSortBy,
  setUserProfileInGatedCommunity,
  deleteUserFromGatedCommunity
} from "src/services/api/gatedCommunities"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import ViewButton from "src/components/table/ViewButton"
import { useDebounce } from "src/utils/useDebounce"
import { useLocalStorage } from "src/utils/useLocalStorage"
import UserService, { Role } from "src/UserService"
import { EditMemberModal } from "./EditMemberModal"
import { MembersFilterBar } from "./MembersFilterBar"
import { Member, MembersTable } from "./MembersTable"
import { useGatedCommunityMembersFetch } from "./useGatedCommunityMembersFetch"
import { useCommunities } from "src/providers/UserContext"
import { StatusChip } from "src/components/table/StatusChip"
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';

function recordToCompanyMember(
  record: GatedCommunityMemberRecord,
  communityName: string
): Member {

  return {
    userId: record.id,
    email: record.email,
    firstName: record.firstName ?? "",
    lastName: record.lastName ?? "",
    profile: record.profile,
    roles: record.roles,
    isPending: record.isPending
  }
}

export const GatedCommunitiesTab = () => {
  const user = useUser()
  const { setUserCommunities, communities, managerCommunities } = useCommunities()
  const { snackbarShowMessage } = useSnackbar()
  const { isOrgAdmin } = useCompany()
  const communityOptions = communities

  const [filters, setFilters] = useLocalStorage<{
    query: string
    selectedCommunityId: string | null
    roleIds: string[]
  }>("portal/gated-communities-filters", { query: "", selectedCommunityId: null, roleIds: [] })

  const query = filters.query ?? ""
  const setQuery = (value: string) => setFilters((prev) => ({ ...prev, query: value }))
  const roles = (filters.roleIds ?? []) as Role[]
  const setRoles = (value: Role[]) => setFilters((prev) => ({ ...prev, roleIds: value }))

  const [selectedCommunityState, setSelectedCommunityState] = useState<Community | null>(null)
  const setSelectedCommunity = (value: Community | null) => {
    setSelectedCommunityState(value)
    setFilters((prev) => ({
      ...prev,
      selectedCommunityId: value?.gatedCommunityId ?? null
    }))
  }

  const debouncedQuery = useDebounce(query, 400)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [order, setOrder] = useState<"asc" | "desc">("asc")
  const [orderBy, setOrderBy] = useState<GatedCommunityMembersSortBy>("LAST_NAME")
  const [refreshKey, setRefreshKey] = useState(0)
  const [editMember, setEditMember] = useState<Member | null>(null)
  const [deactivateMember, setDeactivateMember] = useState<Member | null>(null)
  const [addCommunityOpen, setAddCommunityOpen] = useState(false)
  const [addMembersOpen, setAddMembersOpen] = useState(false)

  useEffect(() => {
    if (managerCommunities.length === 0) return
    const fromFilters = managerCommunities.find((c) => c.gatedCommunityId === filters.selectedCommunityId)
    const next = fromFilters ?? managerCommunities[0]
    setSelectedCommunityState((prev) => (prev === null ? next : prev.gatedCommunityId === next.gatedCommunityId ? prev : next))
  }, [managerCommunities, filters.selectedCommunityId])

  const selectedCommunity = selectedCommunityState ?? managerCommunities.find((c) => c.gatedCommunityId === filters.selectedCommunityId) ?? managerCommunities[0] ?? null

  const { members: records, totalCount, loading } = useGatedCommunityMembersFetch({
    gatedCommunityId: selectedCommunity?.gatedCommunityId,
    page,
    rowsPerPage,
    orderBy,
    order,
    name: debouncedQuery,
    roles,
    refreshKey,
    enabled: !!selectedCommunity
  })

  const members: Member[] =
    selectedCommunity == null
      ? []
      : records.map((r) =>
        recordToCompanyMember(r, selectedCommunity.gatedCommunityName)
      )

  const handleRequestSort = (property: GatedCommunityMembersSortBy) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
    setPage(0)
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const clearFilters = () => {
    setFilters((prev) => ({ ...prev, query: "", roleIds: [] }))
    setPage(0)
  }

  const handleChangeProfile = (member: Member) => {
    if (!selectedCommunity || !member.userId) return
    const newProfile = member.profile === "GATED_COMMUNITY_ADMIN" ? "GATED_COMMUNITY_MEMBER" : "GATED_COMMUNITY_ADMIN"
    setUserProfileInGatedCommunity(selectedCommunity.gatedCommunityId, member.userId, newProfile)
      .then(() => {
        snackbarShowMessage("Profile updated", "success")
        setRefreshKey((k) => k + 1)
      })
      .catch(() => snackbarShowMessage("Failed to update profile", "error"))
  }

  const handleRefreshCommunities = () => {
    if (user?.login) {
      getUserCommunities(user.login).then(setUserCommunities)
    }
  }

  const communityFilterSlot = (
    <CustomAutocomplete<Community>
      value={selectedCommunity}
      options={communityOptions}
      getOptionLabel={(opt) => opt.gatedCommunityName}
      isOptionEqualToValue={(a, b) => a.gatedCommunityId === b.gatedCommunityId}
      getOptionDisabled={(opt) => opt.profile !== "GATED_COMMUNITY_ADMIN"}
      onChange={(_, val) => setSelectedCommunity(val as Community)}
      label="Knowledge Base"
      size="small"
      disableClearable={managerCommunities.length > 0}
      disabled={managerCommunities.length === 0}
    />
  )

  const handleRemoveMember = (member: Member) => {
    setDeactivateMember(member)
  }

  const handleDeactivateConfirm = (member: Member) => {
    if (!member.userId) return
    setDeactivateMember(null)
    const request = member.profile === null
      ? deleteInvitation(member.userId)
      : deleteUserFromGatedCommunity(selectedCommunity!.gatedCommunityId, member.userId)
    request
      .then(() => {
        snackbarShowMessage(member.profile === null ? "Invitation removed" : "Member removed", "success")
        setRefreshKey((k) => k + 1)
      })
      .catch(() => snackbarShowMessage(member.profile === null ? "Failed to remove invitation" : "Failed to remove member", "error"))
  }

  const handleResendInvitation = (member: Member) => {
    resendInvitation(member.userId)
      .then(() => snackbarShowMessage("Invitation resent", "success"))
      .catch(() => snackbarShowMessage("Failed to resend invitation", "error"))
  }

  const rightSlot = <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: "auto" }}>
    {isOrgAdmin &&
      <Button
        variant="contained"
        color="primary"
        onClick={() => setAddCommunityOpen(true)}
      >
        Add Knowledge Base
      </Button>}
    <Button
      variant="contained"
      color="primary"
      onClick={() => setAddMembersOpen(true)}
      disabled={!selectedCommunity || selectedCommunity.gatedCommunityName === "My Knowledge Base"}
    >
      Add member
    </Button>
  </Stack>

  return (
    <Stack sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <MembersFilterBar
        query={query}
        onQueryChange={setQuery}
        communityFilterSlot={communityFilterSlot}
        selectedRoleIds={roles}
        onRolesChange={setRoles}
        onClearFilters={clearFilters}
        rightSlot={rightSlot}
      />
      <MembersTable
        members={members}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        orderBy={orderBy as CompanyMembersSortBy}
        order={order}
        onRequestSort={(property) => handleRequestSort(property as GatedCommunityMembersSortBy)}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        showGatedCommunitiesColumn={false}
        renderProfileCell={(member) => {
          return member.profile === "GATED_COMMUNITY_ADMIN" ? (
            <Typography variant="inherit" fontWeight="bold">
              Manager
            </Typography>
          ) : member.isPending ? (
            <StatusChip label="PENDING" />
          ) : (
            "Member"
          )
        }}
        renderActions={(member) => {
          return (member.isPending ? (
            <Stack spacing={0.5} alignItems="left">
              <Button size="small" variant="outlined" color="secondary" onClick={() => handleResendInvitation(member)}>Resend invitation</Button>
              <ViewButton size="small" aria-label="Remove member" onClick={() => handleRemoveMember(member)}
                sx={{ width: "fit-content" }}>
                <PersonOffOutlinedIcon fontSize="small" />
              </ViewButton>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ViewButton size="small" aria-label="Edit member" onClick={() => handleRemoveMember(member)}
                disabled={!!(user?.email && member.email && user.email === member.email)}>
                <PersonOffOutlinedIcon fontSize="small" />
              </ViewButton>
              <ViewButton
                size="small"
                aria-label="Change profile"
                onClick={() => handleChangeProfile(member)}
                disabled={!!(user?.email && member.email && user.email === member.email)}
              >
                {member.profile === "GATED_COMMUNITY_ADMIN" ? (
                  <ArrowDownwardOutlinedIcon fontSize="small" />
                ) : (
                  <ArrowUpwardOutlinedIcon fontSize="small" />
                )}
              </ViewButton>
              <ViewButton size="small" aria-label="Edit member" onClick={() => setEditMember(member)}>
                <EditOutlinedIcon fontSize="small" />
              </ViewButton>
            </Stack>
          )
          )
        }}
        tableAriaLabel="Knowledge Base members table"
      />
      <DeactivateMemberDialog
        member={deactivateMember}
        title={deactivateMember?.isPending ? "Remove Invitation?" : "Remove Member?"}
        bodyText={deactivateMember?.isPending ? (
          <>Are you sure you want to remove the invitation for <strong>{deactivateMember?.email}</strong>?</>
        ) : (
          <>Are you sure you want to remove <strong>{deactivateMember?.firstName} {deactivateMember?.lastName}</strong> from the <strong>{selectedCommunity?.gatedCommunityName}</strong>?</>
        )}
        buttonText={deactivateMember?.isPending ? "Yes, remove invitation" : "Yes, remove member"}
        onConfirm={handleDeactivateConfirm}
        onClose={() => setDeactivateMember(null)}
      />
      {editMember && <EditMemberModal
        open={editMember !== null}
        member={editMember}
        communityId={selectedCommunity?.gatedCommunityId}
        onClose={() => setEditMember(null)}
        onUpdated={() => setRefreshKey((k) => k + 1)}
      />
      }
      <AddGatedCommunityDialog
        open={addCommunityOpen}
        onClose={() => setAddCommunityOpen(false)}
        onCreated={handleRefreshCommunities}
      />
      <AddMembersToCommunityDialog
        open={addMembersOpen}
        community={selectedCommunity}
        onClose={() => setAddMembersOpen(false)}
        onAdded={() => setRefreshKey((k) => k + 1)}
      />
    </Stack>
  )
}
